import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, UserPlus, CheckCircle2, Trash2, ExternalLink } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';

const statusClass = {
  new: 'badge-blue',
  contacted: 'badge-purple',
  qualified: 'badge-green',
  lost: 'badge-red',
  converted: 'badge-gray',
};

const statusLabels = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  lost: 'Lost',
  converted: 'Converted',
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const { showToast } = useToast();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    axios.get('/api/leads', { params: { status, search: search || undefined } })
      .then(res => setLeads(res.data.data))
      .catch(err => showToast(err.response?.data?.message || 'Failed to load leads', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);

  const stats = useMemo(() => ({
    new: leads.filter(l => l.status === 'new').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
    converted: leads.filter(l => l.status === 'converted').length,
  }), [leads]);

  const updateStatus = async (lead, nextStatus) => {
    try {
      await axios.put(`/api/leads/${lead.id}`, { status: nextStatus });
      showToast('Lead updated', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update lead', 'error');
    }
  };

  const convertLead = async (lead) => {
    if (!window.confirm(`Convert "${lead.contactPerson}" to a client?`)) return;
    try {
      const res = await axios.post(`/api/leads/${lead.id}/convert`);
      showToast('Lead converted to client', 'success');
      navigate(`/clients/${res.data.data.id}`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to convert lead', 'error');
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.contactPerson}"?`)) return;
    try {
      await axios.delete(`/api/leads/${lead.id}`);
      showToast('Lead deleted', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete lead', 'error');
    }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  return (
    <div className="page-content">
      <div className="flex-between mb-24">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Leads</h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Website form submissions and potential clients</p>
        </div>
        <button className="btn btn-secondary" onClick={load}><Search size={16} /> Refresh</button>
      </div>

      <div className="grid-3 mb-24">
        <div className="stat-card"><UserPlus /><div className="stat-value">{stats.new}</div><div className="stat-label">New Leads</div></div>
        <div className="stat-card"><CheckCircle2 /><div className="stat-value">{stats.qualified}</div><div className="stat-label">Qualified</div></div>
        <div className="stat-card"><ExternalLink /><div className="stat-value">{stats.converted}</div><div className="stat-label">Converted</div></div>
      </div>

      <div className="card mb-24">
        <div className="grid-2">
          <div className="input-group" style={{ marginBottom: 0 }}>
            <label className="input-label">Search</label>
            <input className="input" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Name, company, email or service..." />
          </div>
          <div className="input-group" style={{ marginBottom: 0 }}>
            <label className="input-label">Status</label>
            <select className="input select" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="all">All</option>
              {Object.keys(statusLabels).map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
            </select>
          </div>
        </div>
      </div>

      {!leads.length ? <div className="empty-state"><UserPlus /><p>No leads yet</p></div> : (
        <div className="card table-wrap">
          <table className="finance-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Contact</th>
                <th>Interested In</th>
                <th>Budget</th>
                <th>Status</th>
                <th>Source</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{lead.companyName || lead.contactPerson}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{lead.contactPerson}</div>
                  </td>
                  <td>
                    <div>{lead.email}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{lead.phone || '-'}</div>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <div>{lead.serviceInterest || '-'}</div>
                    {lead.message && <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>{lead.message}</div>}
                  </td>
                  <td>{lead.budget || '-'}</td>
                  <td>
                    <select className="input select" style={{ minWidth: 130 }} value={lead.status} onChange={e => updateStatus(lead, e.target.value)} disabled={lead.status === 'converted'}>
                      {Object.keys(statusLabels).map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                    </select>
                  </td>
                  <td><span className="badge badge-gray">{lead.source || 'website'}</span></td>
                  <td>{new Date(lead.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <span className={`badge ${statusClass[lead.status] || 'badge-gray'}`}>{statusLabels[lead.status] || lead.status}</span>
                      {lead.status !== 'converted' && (
                        <button className="btn btn-primary btn-sm" onClick={() => convertLead(lead)}>Convert</button>
                      )}
                      {lead.convertedClientId && (
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/clients/${lead.convertedClientId}`)}>Client</button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => deleteLead(lead)} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Leads;
